import React from 'react'
import { Col, Row } from 'reactstrap'
import {
  Avatar,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItemButton,
  ListItemText,
  Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import moment from 'moment'
import { buildReservationSummary } from '../utils/activityReservations'

/**
 * The reservation side of the activity link, mirroring MatchDetails for matches.
 * Shows which activity a court booking belongs to, and lets one be attached from
 * here. Both directions go through firebase.linkActivityAndReservation so the
 * activity summary and the reservation flags always move together.
 */
function ActivityAttach({ reservation, enabled, firebase, onChanged, onBusy, occurrenceStart, occurrenceEnd }) {
  const [activity, setActivity] = React.useState(null)
  const [loading, setLoading] = React.useState(false)
  const [pickerOpen, setPickerOpen] = React.useState(false)
  const [candidates, setCandidates] = React.useState([])
  const [loadingCandidates, setLoadingCandidates] = React.useState(false)
  const [working, setWorking] = React.useState(false)

  const activityid = reservation ? reservation.activityid : null
  const reservationid = reservation ? reservation.id : null
  const group = reservation && reservation.resourcegroups ? reservation.resourcegroups[0] : null
  const isRecurring = !!(reservation && reservation.isrecurring)

  // For a recurring reservation, resourcegroups[0].start is the FIRST occurrence of the
  // series, not the one that was clicked in the calendar. The rest of the modal reads
  // the occurrence from selectedResource.start; do the same, or a standing shift would
  // look for activities on the day the series began.
  const slotStart = occurrenceStart || (group ? group.start : null)
  const slotEnd = occurrenceEnd || (group ? group.end : null)

  React.useEffect(() => {
    let cancelled = false
    const load = async () => {
      if (!activityid) {
        setActivity(null)
        return
      }
      setLoading(true)
      const snapshot = await firebase.getActivity(activityid)
      if (cancelled) return
      setActivity(snapshot.exists ? { id: snapshot.id, ...snapshot.data() } : null)
      setLoading(false)
    }
    load()
    return () => {
      cancelled = true
    }
  }, [activityid, firebase])

  if (!enabled) return null

  const openPicker = async () => {
    setPickerOpen(true)
    setLoadingCandidates(true)
    const day = moment.utc(slotStart).format('YYYY-MM-DD')
    const snapshot = await firebase.getActivities()
    const all = []
    snapshot.forEach((doc) => all.push({ id: doc.id, ...doc.data() }))
    // Same day as the booking: an activity elsewhere in the calendar is almost never
    // the one being attached, and the full list is unusable at any real venue.
    setCandidates(all.filter((a) => a.start && moment.utc(a.start).format('YYYY-MM-DD') === day))
    setLoadingCandidates(false)
  }

  const attach = async (candidate) => {
    setWorking(true)
    if (onBusy) onBusy(true)
    const resource = group && group.resources && group.resources[0] ? group.resources[0] : null
    const summary = buildReservationSummary(
      {
        id: reservationid,
        resourceId: resource ? resource.id : '',
        // The occurrence that was clicked, not the series start.
        start: slotStart,
        end: slotEnd,
        cost: group && group.overridecost !== undefined ? group.overridecost : group ? group.cost : 0,
      },
      resource,
      'existing'
    )
    await firebase.linkActivityAndReservation(candidate.id, summary)
    setWorking(false)
    setPickerOpen(false)
    if (onChanged) await onChanged(candidate.id)
    if (onBusy) onBusy(false)
  }

  const detach = async () => {
    setWorking(true)
    if (onBusy) onBusy(true)
    await firebase.unlinkActivityAndReservation(activityid, reservationid)
    setWorking(false)
    if (onChanged) await onChanged(null)
    if (onBusy) onBusy(false)
  }

  // A reservation must exist before it can be linked: the link is stored by id.
  if (!reservationid) {
    return (
      <Row style={{ marginTop: 15 }}>
        <Col>
          <Typography variant='body2' color='text.secondary'>
            Tallenna varaus ennen aktiviteetin kiinnittämistä.
          </Typography>
        </Col>
      </Row>
    )
  }

  if (activityid) {
    return (
      <div className='border rounded' style={{ padding: 5, marginBottom: 10, backgroundColor: '#f4f3ef' }}>
        <Row>
          <Col>
            <Typography variant='body2' component='div' display='inline' align='left'>
              <b>Aktiviteetti</b>
            </Typography>
          </Col>
          <Col xs={'auto'}>
            <Button size='small' onClick={detach} disabled={working}>
              Irrota
            </Button>
          </Col>
          <Col xs={12}>
            {loading ? (
              <CircularProgress size={14} />
            ) : activity ? (
              <Typography variant='body2' component='div' align='left'>
                {activity.name && activity.name.fi ? activity.name.fi : activity.id}
                {activity.start ? ' — ' + moment.utc(activity.start).format('D.M.YYYY HH:mm') : ''}
              </Typography>
            ) : (
              <Typography variant='body2' color='text.secondary'>
                Liitettyä aktiviteettia ei löytynyt.
              </Typography>
            )}
          </Col>
        </Row>
      </div>
    )
  }

  // A recurring reservation is a single document with recurrencyDates[], so activityid
  // would apply to the whole series rather than the occurrence in front of you. Rather
  // than offer a link with surprising reach, do not offer it at all.
  if (isRecurring) {
    return (
      <Row style={{ marginTop: 15 }}>
        <Col>
          <Typography variant='body2' color='text.secondary'>
            Toistuvaan varaukseen ei voi kiinnittää aktiviteettia.
          </Typography>
        </Col>
      </Row>
    )
  }

  return (
    <>
      <Row style={{ marginTop: 15 }}>
        <Col xs='1'>
          <Avatar onClick={openPicker} style={{ cursor: 'pointer', height: 20, width: 20 }}>
            <AddIcon />
          </Avatar>
        </Col>
        <Col>
          <div style={{ marginTop: 0, cursor: 'pointer' }} onClick={openPicker}>
            Kiinnitä aktiviteetti
          </div>
        </Col>
      </Row>
      <Dialog open={pickerOpen} onClose={() => setPickerOpen(false)} maxWidth='sm' fullWidth>
        <DialogTitle>Kiinnitä aktiviteetti</DialogTitle>
        <DialogContent>
          {loadingCandidates ? (
            <CircularProgress size={18} />
          ) : candidates.length === 0 ? (
            <Typography variant='body2' color='text.secondary'>
              Päivälle {moment.utc(slotStart).format('D.M.YYYY')} ei löytynyt aktiviteetteja.
            </Typography>
          ) : (
            <List dense>
              {candidates.map((candidate) => (
                <ListItemButton key={candidate.id} disabled={working} onClick={() => attach(candidate)}>
                  <ListItemText
                    primary={candidate.name && candidate.name.fi ? candidate.name.fi : candidate.id}
                    secondary={
                      moment.utc(candidate.start).format('HH:mm') +
                      '-' +
                      moment.utc(candidate.end).format('HH:mm')
                    }
                  />
                </ListItemButton>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPickerOpen(false)} disabled={working}>
            Sulje
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default ActivityAttach
